import {
  argMatchesFilter,
  formatGraphQLType,
  getExplorerChildFields,
  getFieldArgs,
  getFieldOutputTypeName,
  getInputObjectFields,
  getQueryRootFields,
  textMatchesFilter,
  unwrapGraphQLType,
} from './schemaTypes';

/** Paths that must be expanded for every filter match to stay visible. */
export function collectFilterExpandPaths(
  field,
  schemaIndex,
  basePath,
  filter,
  visitedTypes = new Set(),
  expandPaths = new Set(),
) {
  if (!filter) return expandPaths;

  let matchesHere =
    textMatchesFilter(field.name, filter)
    || textMatchesFilter(formatGraphQLType(field.type), filter);

  for (const arg of getFieldArgs(field)) {
    const argPath = `${basePath}.arg.${arg.name}`;
    if (argMatchesFilter(arg, filter, schemaIndex)) {
      matchesHere = true;
      expandPaths.add(argPath);
    }
  }

  const typeName = getFieldOutputTypeName(field);
  if (typeName && visitedTypes.has(typeName)) {
    if (matchesHere) expandPaths.add(basePath);
    return expandPaths;
  }

  const nextVisited = typeName ? new Set(visitedTypes).add(typeName) : visitedTypes;
  let childMatched = false;

  for (const child of getExplorerChildFields(field, schemaIndex)) {
    const childPath = `${basePath}.${child.name}`;
    const sizeBefore = expandPaths.size;
    collectFilterExpandPaths(child, schemaIndex, childPath, filter, nextVisited, expandPaths);
    if (expandPaths.has(childPath) || expandPaths.size > sizeBefore) {
      childMatched = true;
    }
  }

  if (matchesHere || childMatched) {
    expandPaths.add(basePath);
  }

  return expandPaths;
}

export const MAX_SCHEMA_SEARCH_DEPTH = 5;

export const MAX_VISIBLE_SEARCH_ENTRIES = 800;

export const MAX_BOUNDED_SEARCH_ENTRIES = 600;

export const MAX_SEARCH_MATCHES = 80;

/** Explorer path ancestors, including `.arg.` segments (e.g. Media.arg.sort). */
export function getSchemaPathAncestors(path) {
  const ancestors = [];
  if (!path) return ancestors;

  const argMarker = '.arg.';
  if (path.includes(argMarker)) {
    const [fieldPath, rest] = path.split(argMarker);
    if (fieldPath) ancestors.push(fieldPath);
    if (rest) {
      const subParts = rest.split('.');
      for (let i = 1; i < subParts.length; i += 1) {
        ancestors.push(`${fieldPath}${argMarker}${subParts.slice(0, i).join('.')}`);
      }
    }
    return ancestors;
  }

  const parts = path.split('.');
  for (let i = 1; i < parts.length; i += 1) {
    ancestors.push(parts.slice(0, i).join('.'));
  }
  return ancestors;
}

export function createSearchEntry(path, name, type, kind) {
  const typeLabel = formatGraphQLType(type);
  return {
    path,
    kind,
    name,
    typeLabel,
    nameLower: name.toLowerCase(),
    typeLower: typeLabel.toLowerCase(),
  };
}

export function addFieldSearchEntries(field, path, schemaIndex, entries, seen) {
  if (seen.has(path)) return;
  seen.add(path);
  entries.push(createSearchEntry(path, field.name, field.type, 'field'));

  for (const arg of getFieldArgs(field)) {
    const argPath = `${path}.arg.${arg.name}`;
    if (seen.has(argPath)) continue;
    seen.add(argPath);
    entries.push(createSearchEntry(argPath, arg.name, arg.type, 'arg'));
  }
}

/** Entries for roots + currently expanded branches only (Postman-style visible scope). */
export function collectVisibleSearchEntries(schemaIndex, rootFields, expandedPaths) {
  const entries = [];
  const seen = new Set();

  const walkExpanded = (field, path, visitedTypes) => {
    addFieldSearchEntries(field, path, schemaIndex, entries, seen);
    if (entries.length >= MAX_VISIBLE_SEARCH_ENTRIES) return;

    for (const arg of getFieldArgs(field)) {
      const argPath = `${path}.arg.${arg.name}`;
      if (!expandedPaths.has(argPath)) continue;
      const inner = unwrapGraphQLType(arg.type);
      if (inner?.kind === 'INPUT_OBJECT' && inner.name) {
        for (const inputField of getInputObjectFields(inner.name, schemaIndex)) {
          if (entries.length >= MAX_VISIBLE_SEARCH_ENTRIES) return;
          const inputPath = `${argPath}.${inputField.name}`;
          if (seen.has(inputPath)) continue;
          seen.add(inputPath);
          entries.push(createSearchEntry(inputPath, inputField.name, inputField.type, 'input'));
        }
      }
    }

    if (!expandedPaths.has(path)) return;

    const typeName = getFieldOutputTypeName(field);
    if (typeName && visitedTypes.has(typeName)) return;
    const nextVisited = typeName ? new Set(visitedTypes).add(typeName) : visitedTypes;

    for (const child of getExplorerChildFields(field, schemaIndex)) {
      walkExpanded(child, `${path}.${child.name}`, nextVisited);
      if (entries.length >= MAX_VISIBLE_SEARCH_ENTRIES) return;
    }
  };

  for (const root of rootFields) {
    walkExpanded(root, root.name, new Set());
    if (entries.length >= MAX_VISIBLE_SEARCH_ENTRIES) return entries;
  }

  return entries;
}

/** Roots + descendants up to a fixed depth (cheap wide scan). */
export function collectShallowSearchEntries(schemaIndex, rootFields, maxDepth = 2) {
  const entries = [];
  const seen = new Set();

  const walk = (field, path, visitedTypes, depth) => {
    if (depth > maxDepth) return;

    addFieldSearchEntries(field, path, schemaIndex, entries, seen);

    const typeName = getFieldOutputTypeName(field);
    if (typeName && visitedTypes.has(typeName)) return;
    const nextVisited = typeName ? new Set(visitedTypes).add(typeName) : visitedTypes;

    for (const child of getExplorerChildFields(field, schemaIndex)) {
      walk(child, `${path}.${child.name}`, nextVisited, depth + 1);
    }
  };

  for (const root of rootFields) {
    walk(root, root.name, new Set(), 0);
  }

  return entries;
}

export function fieldNameMatchesAnyToken(field, tokens) {
  const nameLower = field.name.toLowerCase();
  return tokens.some((token) => nameLower.includes(token));
}

/** Deep scan only under branches whose field names match the filter tokens. */
export function collectMatchingBranchSearchEntries(
  schemaIndex,
  rootFields,
  filter,
  maxDepth = MAX_SCHEMA_SEARCH_DEPTH,
) {
  const tokens = filter.split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];

  const entries = [];
  const seen = new Set();

  const walk = (field, path, visitedTypes, depth, branchActive) => {
    if (depth > maxDepth || entries.length >= MAX_BOUNDED_SEARCH_ENTRIES) return;

    const nameMatches = fieldNameMatchesAnyToken(field, tokens);
    const active = branchActive || nameMatches;

    addFieldSearchEntries(field, path, schemaIndex, entries, seen);

    const typeName = getFieldOutputTypeName(field);
    if (typeName && visitedTypes.has(typeName)) return;
    const nextVisited = typeName ? new Set(visitedTypes).add(typeName) : visitedTypes;

    for (const child of getExplorerChildFields(field, schemaIndex)) {
      const childMatches = fieldNameMatchesAnyToken(child, tokens);
      if (active || childMatches || depth < 1) {
        walk(child, `${path}.${child.name}`, nextVisited, depth + 1, active);
        if (entries.length >= MAX_BOUNDED_SEARCH_ENTRIES) return;
      }
    }
  };

  for (const root of rootFields) {
    walk(root, root.name, new Set(), 0, fieldNameMatchesAnyToken(root, tokens));
    if (entries.length >= MAX_BOUNDED_SEARCH_ENTRIES) break;
  }

  return entries;
}

/** Per-root bounded DFS so large schemas are not dominated by the first root field. */
export function collectBoundedSearchEntries(schemaIndex, rootFields, maxDepth = MAX_SCHEMA_SEARCH_DEPTH) {
  const entries = [];
  const seen = new Set();
  const rootCount = Math.max(rootFields.length, 1);
  const budgetPerRoot = Math.max(40, Math.floor(MAX_BOUNDED_SEARCH_ENTRIES / rootCount));

  const walk = (field, path, visitedTypes, depth, budget) => {
    if (depth > maxDepth || budget.count <= 0 || entries.length >= MAX_BOUNDED_SEARCH_ENTRIES) return;

    addFieldSearchEntries(field, path, schemaIndex, entries, seen);
    budget.count -= 1;
    if (budget.count <= 0) return;

    const typeName = getFieldOutputTypeName(field);
    if (typeName && visitedTypes.has(typeName)) return;
    const nextVisited = typeName ? new Set(visitedTypes).add(typeName) : visitedTypes;

    for (const child of getExplorerChildFields(field, schemaIndex)) {
      walk(child, `${path}.${child.name}`, nextVisited, depth + 1, budget);
      if (budget.count <= 0) return;
    }
  };

  for (const root of rootFields) {
    walk(root, root.name, new Set(), 0, { count: budgetPerRoot });
    if (entries.length >= MAX_BOUNDED_SEARCH_ENTRIES) break;
  }

  return entries;
}

/** Score a filter against an entry — all tokens must match name or type. */
export function scoreSearchEntry(entry, filter) {
  const tokens = filter.split(/\s+/).filter(Boolean);
  if (!tokens.length) return 0;

  let score = 0;
  for (const token of tokens) {
    const { nameLower, typeLower } = entry;
    if (nameLower === token) score += 12;
    else if (nameLower.startsWith(token)) score += 8;
    else if (nameLower.includes(token)) score += 5;
    else if (typeLower.includes(token)) score += 2;
    else return -1;
  }
  return score;
}

export function pickSearchMatches(entries, filter, limit = MAX_SEARCH_MATCHES) {
  const ranked = [];
  for (const entry of entries) {
    const score = scoreSearchEntry(entry, filter);
    if (score >= 0) ranked.push({ entry, score });
  }
  ranked.sort((a, b) => b.score - a.score || a.entry.path.length - b.entry.path.length);
  return ranked.slice(0, limit).map((r) => r.entry);
}

export function buildSearchFilterResult(matches) {
  const matchingPaths = new Set();
  const visiblePaths = new Set();
  const expandPaths = new Set();

  for (const entry of matches) {
    matchingPaths.add(entry.path);
    visiblePaths.add(entry.path);
    for (const ancestor of getSchemaPathAncestors(entry.path)) {
      visiblePaths.add(ancestor);
      expandPaths.add(ancestor);
    }
  }

  return { matchingPaths, visiblePaths, expandPaths, matches };
}

export function mergeSearchMatches(existing, entries, filter, seen) {
  const extra = pickSearchMatches(
    entries.filter((e) => !seen.has(e.path)),
    filter,
    MAX_SEARCH_MATCHES - existing.length,
  );
  for (const match of extra) seen.add(match.path);
  return extra.length ? [...existing, ...extra] : existing;
}

/**
 * Postman-style search: visible/expanded tree first, then shallow scan, branch-targeted
 * deep scan, and finally a per-root bounded fallback.
 */
export function searchSchemaExplorer({ schemaIndex, rootFields, expandedPaths, filter }) {
  if (!filter) return null;

  const seen = new Set();
  let matches = [];

  const addTier = (entries) => {
    if (matches.length >= MAX_SEARCH_MATCHES) return;
    matches = mergeSearchMatches(matches, entries, filter, seen);
  };

  addTier(collectVisibleSearchEntries(schemaIndex, rootFields, expandedPaths));
  addTier(collectShallowSearchEntries(schemaIndex, rootFields, 2));
  addTier(collectMatchingBranchSearchEntries(schemaIndex, rootFields, filter));
  addTier(collectBoundedSearchEntries(schemaIndex, rootFields));

  return buildSearchFilterResult(matches);
}

/** @deprecated Use searchSchemaExplorer — kept for tests migrating off full index. */
export function buildSchemaSearchIndex(schema, schemaIndex) {
  return collectBoundedSearchEntries(schemaIndex, getQueryRootFields(schema, schemaIndex));
}

/** @deprecated Use searchSchemaExplorer. */
export function filterSchemaSearchIndex(entries, filter) {
  if (!filter) return null;
  return buildSearchFilterResult(pickSearchMatches(entries, filter));
}
